import type { InferGetStaticPropsType } from 'next';
import { lazy, Suspense, useCallback, useMemo } from 'react';

import { getRegister } from 'services/idm';

import { apiErrorHandler } from '@login/helpers';
import { getLocaleStaticProps } from '@login/helpers/static-props';
import { useIdmPage } from '@login/hooks/use-idm-page';

const RegisterForm = lazy(
	() => import('@login/patterns/register-form/register-form')
);

export const getStaticProps = getLocaleStaticProps;

const Register = ({
	locale,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
	const onError = useCallback(
		(error: unknown) => apiErrorHandler(error, locale),
		[locale]
	);

	const {
		pageData: registerData,
		shouldDisplay,
		token,
	} = useIdmPage({
		queryKey: ['register', locale],
		queryFn: () => getRegister(locale),
		enabled: !!locale,
		onError,
	});

	// Memoised so the form does not receive a fresh `urls` object on every
	// parent render.
	const urls = useMemo(
		() => ({
			registerUrl: registerData?.placeholders?.registerUrl,
			signInUrl: registerData?.placeholders?.signInUrl,
			forgottenPasswordUrl:
				registerData?.placeholders?.forgottenPasswordUrl,
			privacyPolicyUrl: registerData?.placeholders?.privacyPolicyUrl,
			supportName: registerData?.placeholders?.supportName,
		}),
		[registerData?.placeholders]
	);

	if (!shouldDisplay || !registerData) return null;

	return (
		<Suspense fallback={null}>
			<RegisterForm
				urls={urls}
				marketingMessage={registerData.marketingMessage}
				disclaimerMessage={registerData.disclaimerMessage}
				token={token}
				showChallengeForm={registerData.showChallengeForm}
				minimumAge={registerData.minimumAge}
			/>
		</Suspense>
	);
};

export default Register;
